import '@neo4j-ndl/base/lib/neo4j-ds-styles.css';
import { analyticsAdapter } from '@nx/analytics-service';
import { isInvalidUrlConnectionError } from '@nx/errors';
import { LaunchDarklyAdapter } from '@nx/launch-darkly-service';
import type { AbstractLogger } from '@nx/logger';
import type { TokenProviders } from '@nx/neo4j-token-provider';
import type { AppManifest, Capability, selectConfiguration } from '@nx/state';
import {
  Analytics,
  ErrorTracking,
  StoreKit,
  consentToTracking,
  fetchLocalDiscoveryData,
  getBaseUrl,
  initializeSsoRedirectUri,
  neo4jDriverAdapter,
  overrideInitialStateFeatureFlag,
  overrideInitialStatePlaylist,
  setupInitialDbConnection,
  startAppUpdateChecks,
  storeReady,
} from '@nx/state';
import { overrideInitialStateDesktop } from '@nx/state/desktop';
import * as Sentry from '@sentry/react';
import * as React from 'react';
import * as ReactDOMClient from 'react-dom/client';

import { FrameworkProvider } from './framework-provider';
import { parseSearchParams } from './search-params';
import type { AuthConfig } from './setup/setup-session';
import { setupAppLogin } from './setup/setup-session';

const enableMocking = async ({
  logger,
  planType,
  isFirstTimeUser = false,
  isMarketplace = false,
}: {
  logger: AbstractLogger;
  isFirstTimeUser?: boolean;
  isMarketplace?: boolean;
  planType: 'enterprise' | 'self-serve';
}) => {
  // `worker.start()` returns a Promise that resolves
  // once the Service Worker is up and ready to intercept requests.
  logger.info('Starting the mocking worker');
  const { loadWorker } = await import('@nx/api-mocks');
  const worker = await loadWorker({ planType, isFirstTimeUser, isMarketplace });

  return worker?.start({
    onUnhandledRequest: 'bypass',
  });
};

export type FrameworkInitOptions = {
  appManifest?: AppManifest;
  auth?: AuthConfig;
  capabilities: Partial<Record<Capability, boolean>>;
  configuration: ReturnType<typeof selectConfiguration>['internal'] & {
    guidesBaseUrl?: string;
    ssoRedirectPath: string;
  };
  // If true, the app will check for new versions by periodicaly fetching 'manifest.json' and notify the user
  enableAppUpdateCheck?: boolean;
  logger: AbstractLogger;
  preRender?: (() => Promise<void>) | (() => void);
  skipReconnect?: boolean;
  tokenProviders?: TokenProviders;
};

export async function frameworkInit(
  app: React.ReactNode,
  {
    appManifest = {},
    auth,
    capabilities,
    configuration,
    logger,
    preRender,
    skipReconnect = false,
    tokenProviders,
  }: FrameworkInitOptions,
) {
  const searchParams = parseSearchParams(window.location.href);
  const capabilitiesList: Capability[] = Object.entries(capabilities).reduce(
    (list: Capability[], [capability, state]) => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      return state ? [...list, capability as Capability] : list;
    },
    [],
  );
  const { persistor, store } = StoreKit.createStore({
    adapters: {
      // TODO: Receive adapter via frameworkInit instead of
      // using singleton from the package
      analytics: analyticsAdapter,
      // TODO: Receive adapter via frameworkInit instead of
      // using singleton from the package
      driver: neo4jDriverAdapter,
      logger,
      tokenProviders,
      ldAdapter: new LaunchDarklyAdapter(),
    },
    initialData: {
      capabilities: capabilitiesList,
      configuration: { appManifest, ...configuration },
      guidePlaylists: {
        baseUrl: configuration.guidesBaseUrl,
      },
    },
  });

  try {
    await storeReady(persistor);
    logger.debug('Store is ready');

    // Updating state from URL params must be done after redux-persist is
    // initialized, or local storage values will overwrite whatever is set
    // through the URL params.
    store.dispatch(overrideInitialStateFeatureFlag(searchParams.featureFlags));
    store.dispatch(
      overrideInitialStateDesktop({
        desktopContext: searchParams.relateConfig !== null,
        relate: searchParams.relateConfig ?? undefined,
      }),
    );
    store.dispatch(
      overrideInitialStatePlaylist({
        currentPlaylist: searchParams.playlist ?? undefined,
      }),
    );
    if (searchParams.userId !== null) {
      store.dispatch(consentToTracking(searchParams.userId));
    }

    if (appManifest.version !== undefined) {
      store.dispatch(startAppUpdateChecks());
    }

    if (capabilitiesList.includes('framework:mock-api')) {
      const isEnterprise = capabilitiesList.includes('framework:mock-enterprise');
      const isFirstTimeUser = capabilitiesList.includes('framework:mock-first-time-user');
      const isMarketplace = capabilitiesList.includes('framework:mock-first-time-marketplace-user');

      await enableMocking({
        logger,
        planType: isEnterprise ? 'enterprise' : 'self-serve',
        isFirstTimeUser,
        isMarketplace,
      }).catch((e: Error) => logger.warn('Could not enable API mocking', e));
    }

    // Persist SSO redirect URI
    const baseUrl = location.origin + getBaseUrl();
    const redirectUrl = new URL(configuration.ssoRedirectPath.replace(/^\//, ''), baseUrl);
    initializeSsoRedirectUri(redirectUrl.toString());

    try {
      // Analytics and Error tracking are depending on network policies
      // and setup could be deferred until network policies are ready.
      void store.dispatch(Analytics.setupIfAllowed());
      ErrorTracking.setupIfAllowed();
    } catch (error) {
      logger.warn('Could not initialize analytics', { error });
    }

    await setupAppLogin(logger, auth);

    if (!skipReconnect) {
      // The DB connection must run after the app login, as some of the connection
      // methods require the user to be logged in.
      void store
        .dispatch(setupInitialDbConnection())
        .unwrap()
        .catch((error: Error) => {
          logger.warn('Could not setup initial DB connection', { error });

          if (!isInvalidUrlConnectionError(error)) {
            // TODO: Revisit error handling later based on Sentry data
            // Right now we just ignore attempts to restore connection to a
            // paused instance with `isInvalidUrlConnectionError`
            Sentry.captureException(error, (scope) => {
              scope.setTags({
                action: 'restore_connection',
              });

              return scope;
            });
          }
        });

      // Check if there is a connection defined on discovery.json
      void store
        .dispatch(fetchLocalDiscoveryData())
        .unwrap()
        .catch(() => logger.info('Local discovery configuration not found'));
    }

    if (preRender !== undefined) {
      await preRender();
    }
  } catch (error) {
    logger.error('App initialization error', { error });
  } finally {
    const rootElement = document.getElementById('root');
    const reactTree = (
      <React.StrictMode>
        <FrameworkProvider logger={logger} persistor={persistor} store={store}>
          {app}
        </FrameworkProvider>
      </React.StrictMode>
    );

    if (rootElement !== null) {
      const root = ReactDOMClient.createRoot(rootElement);
      root.render(reactTree);
    }
  }
}
